import { AssetPathEncodeState } from '@gnsx/genesys.js';

import { AbstractProvider } from './abstract-provider';
import { StorageProviderType } from './interfaces';

import type {
  FileChangeEvent,
  FileDownloadOptions,
  FileExistsOptions,
} from './interfaces';
import type { AssetPath, FileItem, FileListResult } from '@gnsx/genesys.js';

// Simple in-memory cache for the assets structure
let manifest: any[] | null = null;
type fileType = 'file' | 'folder';

export class PublicFolderProvider extends AbstractProvider {
  static clearCache() {
    manifest = null;
  }

  override get providerType() {
    return StorageProviderType.PUBLIC_FOLDER;
  }

  requireProjectContext(): boolean {
    return false;
  }

  override onFileSystemChange(_callback: (event: FileChangeEvent) => void): () => void {
    // Not applicable for PublicFolderProvider as it uses a static manifest
    return () => {
      // No-op unsubscribe
    };
  }

  private async ensureManifest() {
    if (manifest === null) {
      const url = '/api/assets';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`PublicFolderProvider:Failed to list files: ${res.statusText}`);
      const data = await res.json();
      manifest = data.assets ?? [];
    }
  }

  private getDirContents(assets: any[], targetPath: string): any[] {
    const parts = targetPath.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
    let current = assets;
    for (const part of parts) {
      const folder = current.find((item: any) => item.type === 'folder' && item.name === part);
      if (!folder) return [];
      current = folder.children ?? [];
    }
    return current;
  }

  override async doListFiles(dirPath: AssetPath, recursive = false): Promise<FileListResult> {
    // Fetch and cache the assets structure if not already cached
    await this.ensureManifest();

    const subtree = this.getDirContents(manifest ?? [], dirPath.initialPath);
    // Flatten as before
    function flatten(assets: any[]): FileListResult {
      const files: FileItem[] = [];
      const directories: FileItem[] = [];
      for (const item of assets) {
        if (item.type === 'file') {
          files.push({
            name: item.name,
            path: item.path,
            absolutePath: item.path,
            size: item.size ?? 0,
            modifiedTime: item.modifiedTime ? new Date(item.modifiedTime) : new Date(),
            isDirectory: false,
          });
        } else if (item.type === 'folder') {
          directories.push({
            name: item.name,
            path: item.path,
            absolutePath: item.path,
            size: item.size ?? 0,
            modifiedTime: item.modifiedTime ? new Date(item.modifiedTime) : new Date(),
            isDirectory: true,
          });
          if (recursive && item.children && Array.isArray(item.children)) {
            const sub = flatten(item.children);
            files.push(...sub.files);
            directories.push(...sub.directories);
          }
        }
      }
      return { files, directories };
    }
    return flatten(subtree);
  }

  override async downloadFile(filePath: AssetPath, options?: FileDownloadOptions)
    : Promise<Blob | string | ArrayBuffer> {
    filePath = await this.resolvePath(filePath);
    const response = await fetch(filePath.getResolvedPath());
    if (!response.ok) throw new Error(`Failed to download file: ${response.statusText}`);

    if (options?.asBlob) {
      return await response.blob();
    }

    const textContent = await this.extractTextContent(response, filePath.initialPath);
    if (textContent !== null) {
      return textContent;
    }

    return await response.arrayBuffer();
  }

  override async resolvePath(path: AssetPath): Promise<AssetPath> {
    if (path.isResolved()) {
      return path;
    }
    path.resolvePath(path.initialPath, AssetPathEncodeState.Unknown);
    return path;
  }

  override async uploadFile(): Promise<{ path: string; name: string }> {
    throw new Error('Cannot upload files to public folder');
  }

  override async deleteItem(): Promise<void> {
    throw new Error('Cannot delete files from public folder');
  }

  override async createDirectory(): Promise<void> {
    throw new Error('Cannot create directories in public folder');
  }

  override async exists(filePath: AssetPath, options?: FileExistsOptions): Promise<boolean> {
    await this.ensureManifest();

    // 'a/b/c/d.glb'
    const parts = filePath.initialPath.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
    if (parts.length === 0) return false;

    const subtree = this.getDirContents(manifest ?? [], parts.slice(0, -1).join('/'));
    const lastPart = parts[parts.length - 1];
    const file = subtree.find((item: any) => item.type === 'file' && item.name === lastPart);
    return file !== undefined;
  }
}
